/**
 * #427 W2 — Job-function chip.
 *
 * A shift's `role` is a JOB FUNCTION (what this person is DOING for these
 * hours), not the RBAC login role. Jorge can log in as `groomer` and be
 * scheduled as `receptionist` for the 06:00 open — that changes what he
 * DOES, never what he can ACCESS. Copy in this component must never imply
 * otherwise.
 *
 * Design Philosophy principles served:
 *   - Anti-pattern "internal identifiers shown raw to users": the chip
 *     renders STAFF_ROLE_LABELS, never the enum (`kennel_tech` → "Kennel").
 *     An unrecognized value (a role shipped by the server before the client
 *     knows about it) is humanized, never printed raw.
 *   - P6 (two-layer tokens): the chip is coloured from the STRUCTURAL
 *     neutral scale only. Job function is a CATEGORY, not a status and not
 *     an identity — so it may use neither the status families
 *     (--ferro-success/-warning/-error/-info/-stale, which carry status
 *     meaning) nor the tenant's --brand-* colours. The label carries the
 *     meaning; the chip carries the scanability.
 */
import { STAFF_ROLE_LABELS, type StaffRole } from '../../lib/api.js';

const KNOWN_ROLES = new Set<string>(Object.keys(STAFF_ROLE_LABELS));

/**
 * Human label for a job function. Known values come from the shared label
 * map; anything else is title-cased (`front_desk` → "Front desk") so a
 * server-side addition degrades to readable prose instead of leaking an
 * enum string onto the counter's screen.
 */
export function jobFunctionLabel(role: string): string {
  if (KNOWN_ROLES.has(role)) {
    return STAFF_ROLE_LABELS[role as StaffRole];
  }
  const spaced = role.replace(/[_-]+/g, ' ').trim();
  if (spaced.length === 0) return 'General';
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

export type JobFunctionChipProps = {
  readonly role: string;
  readonly className?: string;
};

export function JobFunctionChip({
  role,
  className = '',
}: JobFunctionChipProps): JSX.Element {
  const known = KNOWN_ROLES.has(role);
  const cls = [
    'ferro-jobfn',
    `ferro-jobfn--${known ? role : 'general'}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <span className={cls} data-testid="job-function-chip" data-role={role}>
      {jobFunctionLabel(role)}
    </span>
  );
}
